import { createFileRoute, redirect } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { glass } from "./portal";
import { useIsAdmin } from "@/lib/portal/useIsAdmin";
import { motion, AnimatePresence } from "framer-motion";
import { X, CheckCircle2, Circle, Loader2 } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/portal/admin/projects")({
  component: AdminProjects,
});

type Project = {
  id: string; name: string; owner_id: string; plan: string; status: string;
  current_step: string | null; progress: number; delivery_date: string | null;
  website_url: string | null; project_manager: string | null;
};

function AdminProjects() {
  const { data: isAdmin, isLoading } = useIsAdmin();
  const [active, setActive] = useState<Project | null>(null);

  const { data: projects } = useQuery({
    queryKey: ["admin", "projects-full"],
    enabled: !!isAdmin,
    queryFn: async () => {
      const { data } = await supabase.from("projects").select("*").order("created_at", { ascending: false });
      return (data ?? []) as Project[];
    },
  });

  if (isLoading) return <div className="text-white/60 text-sm">Verifica accesso…</div>;
  if (!isAdmin) throw redirect({ to: "/portal" });

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      <div>
        <div className="text-[10px] uppercase tracking-widest text-fuchsia-300">Admin</div>
        <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight mt-1">Progetti & Timeline</h1>
        <p className="text-white/60 mt-1">Gestisci lo stato di ogni sito cliente e le milestone di consegna.</p>
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {(projects ?? []).map((p) => (
          <button key={p.id} onClick={() => setActive(p)}
            className={`${glass} p-5 text-left hover:border-white/20 transition`}>
            <div className="flex items-center justify-between">
              <div className="font-semibold truncate pr-2">{p.name}</div>
              <span className="text-[10px] uppercase tracking-widest text-white/50">{p.plan}</span>
            </div>
            <div className="mt-3 h-1.5 rounded-full bg-white/5 overflow-hidden">
              <div className="h-full bg-gradient-to-r from-fuchsia-500 to-cyan-400" style={{ width: `${p.progress ?? 0}%` }} />
            </div>
            <div className="mt-2 flex items-center justify-between text-xs text-white/50">
              <span>{p.current_step ?? "—"}</span>
              <span>{p.progress}%</span>
            </div>
            {p.website_url && <div className="mt-2 text-[11px] text-cyan-300 truncate">{p.website_url}</div>}
          </button>
        ))}
      </div>

      <AnimatePresence>
        {active && <ProjectEditor project={active} onClose={() => setActive(null)} />}
      </AnimatePresence>
    </div>
  );
}

function ProjectEditor({ project, onClose }: { project: Project; onClose: () => void }) {
  const qc = useQueryClient();
  const [form, setForm] = useState({
    status: project.status,
    current_step: project.current_step ?? "",
    progress: project.progress,
    website_url: project.website_url ?? "",
    project_manager: project.project_manager ?? "",
  });

  const { data: steps } = useQuery({
    queryKey: ["admin", "steps", project.id],
    queryFn: async () => (await supabase.from("project_timeline_steps").select("*").eq("project_id", project.id).order("step_order")).data ?? [],
  });

  const saveProject = async () => {
    const { error } = await supabase.from("projects").update(form).eq("id", project.id);
    if (error) return toast.error(error.message);
    toast.success("Progetto aggiornato");
    await supabase.from("notifications").insert({
      user_id: project.owner_id,
      title: `Aggiornamento su ${project.name}`,
      body: form.current_step ? `Step attuale: ${form.current_step} (${form.progress}%)` : `Progress: ${form.progress}%`,
      link: "/portal/project",
      type: "project_update",
    });
    qc.invalidateQueries({ queryKey: ["admin", "projects-full"] });
  };

  const setStepStatus = async (id: string, newStatus: string) => {
    await supabase.from("project_timeline_steps").update({
      status: newStatus,
      progress: newStatus === "completed" ? 100 : newStatus === "in_progress" ? 50 : 0,
    }).eq("id", id);
    qc.invalidateQueries({ queryKey: ["admin", "steps", project.id] });
  };

  return (
    <>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose}
        className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50" />
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 20 }}
        className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-full max-w-2xl p-6 rounded-3xl bg-[#0f0f16] border border-white/10 max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-white/40">{project.plan}</div>
            <h3 className="font-semibold text-xl mt-1">{project.name}</h3>
          </div>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-white/10"><X className="h-4 w-4" /></button>
        </div>

        <div className="grid grid-cols-2 gap-3 mb-3">
          <Field label="Status">
            <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="w-full bg-white/5 border border-white/10 rounded-lg px-2 py-2 text-sm">
              {["planning", "in_progress", "review", "live", "paused"].map((s) => <option key={s} value={s} className="bg-black">{s}</option>)}
            </select>
          </Field>
          <Field label={`Progress ${form.progress}%`}>
            <input type="range" min={0} max={100} value={form.progress} onChange={(e) => setForm({ ...form, progress: Number(e.target.value) })} className="w-full" />
          </Field>
          <Field label="Step attuale">
            <input value={form.current_step} onChange={(e) => setForm({ ...form, current_step: e.target.value })} className="w-full bg-white/5 border border-white/10 rounded-lg px-2 py-2 text-sm" />
          </Field>
          <Field label="Project manager">
            <input value={form.project_manager} onChange={(e) => setForm({ ...form, project_manager: e.target.value })} className="w-full bg-white/5 border border-white/10 rounded-lg px-2 py-2 text-sm" />
          </Field>
          <div className="col-span-2">
            <Field label="Website URL">
              <input value={form.website_url} onChange={(e) => setForm({ ...form, website_url: e.target.value })} placeholder="https://" className="w-full bg-white/5 border border-white/10 rounded-lg px-2 py-2 text-sm" />
            </Field>
          </div>
        </div>
        <button onClick={saveProject} className="w-full py-2.5 rounded-xl bg-gradient-to-r from-fuchsia-500 to-cyan-400 text-black text-sm font-semibold mb-6">
          Salva e notifica cliente
        </button>

        <div className="text-xs uppercase tracking-widest text-white/40 mb-2">Timeline milestone</div>
        <div className="space-y-2">
          {(steps ?? []).map((s) => {
            const done = s.status === "completed";
            const inProg = s.status === "in_progress";
            return (
              <div key={s.id} className="flex items-center gap-3 p-3 rounded-xl bg-white/[0.02] border border-white/5">
                <div className="shrink-0">
                  {done ? <CheckCircle2 className="h-5 w-5 text-emerald-400" />
                    : inProg ? <Loader2 className="h-5 w-5 text-fuchsia-400 animate-spin" />
                    : <Circle className="h-5 w-5 text-white/30" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm truncate">{s.name}</div>
                  {s.notes && <div className="text-[11px] text-white/50 truncate">{s.notes}</div>}
                </div>
                <select value={s.status} onChange={(e) => setStepStatus(s.id, e.target.value)}
                  className="text-xs bg-white/5 border border-white/10 rounded-lg px-2 py-1">
                  {["pending", "in_progress", "completed"].map((v) => <option key={v} value={v} className="bg-black">{v.replace("_", " ")}</option>)}
                </select>
              </div>
            );
          })}
          {(steps ?? []).length === 0 && <div className="text-center text-xs text-white/40 py-6">Nessuna milestone ancora</div>}
        </div>
      </motion.div>
    </>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-white/40 mb-1">{label}</div>
      {children}
    </div>
  );
}
